import {
  ActivitySeason,
  AnomalyDirection,
  HOLIDAY_COLORS,
  HOLIDAY_LABELS,
  HolidayKey,
  HolidayWindow,
  SeasonAnomalyProduct,
  WeekSeriesPoint,
} from '../../types/AiForecasting';
import {
  format_hebrew_date,
  gregorian_to_hebrew,
  hebrew_week_index_for_ymd,
  HEBREW_YEAR_WEEKS,
} from './hebrewCalendar';

function hex_to_rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export const SEASON_WASH_ALPHA = 0.32;

/** Same hue as the legend swatch; alpha only so the lines stay visible. */
export function hex_to_rgba(hex: string, alpha: number): string {
  const [r, g, b] = hex_to_rgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function color_for_season_keys(keys: HolidayKey[]): string {
  if (keys.length === 0) {
    return '#9ca3af';
  }
  return HOLIDAY_COLORS[keys[0]];
}

export interface SeasonBand {
  start_index: number;
  end_index: number;
  keys: HolidayKey[];
  label: string;
  color: string;
}

/** HebCal holidays drawn on the year graph. Summer is a gap season, not a holiday band. */
export const HEBCAL_GRAPH_SEASON_KEYS: HolidayKey[] = [
  'tishrei',
  'chanukah',
  'tu_bishvat',
  'purim',
  'pesach',
  'shavuot',
];

function add_days_ymd(ymd: string, days: number): string {
  const dt = new Date(`${ymd}T12:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** Walk inclusive dates so a span that crosses Tishrei does not fill the whole year. */
function mark_ymd_range(marked: boolean[], start: string, end: string): void {
  if (!start || !end || start > end) {
    return;
  }
  let ymd = start;
  for (let step = 0; step < 400 && ymd <= end; step += 1) {
    const index = hebrew_week_index_for_ymd(ymd);
    if (index >= 0 && index < marked.length) {
      marked[index] = true;
    }
    ymd = add_days_ymd(ymd, 1);
  }
}

function paint_keys(windows: HolidayWindow[], holiday_key?: HolidayKey): HolidayKey[] {
  if (holiday_key) {
    return [holiday_key];
  }
  return HEBCAL_GRAPH_SEASON_KEYS.filter((key) => windows.some((window) => window.holiday_key === key));
}

function bands_from_marked(marked: boolean[], key: HolidayKey): SeasonBand[] {
  const bands: SeasonBand[] = [];
  let i = 0;
  while (i < marked.length) {
    if (!marked[i]) {
      i += 1;
      continue;
    }
    let j = i;
    while (j + 1 < marked.length && marked[j + 1]) {
      j += 1;
    }
    bands.push({
      start_index: i,
      end_index: j,
      keys: [key],
      label: HOLIDAY_LABELS[key],
      color: HOLIDAY_COLORS[key],
    });
    i = j + 1;
  }
  return bands;
}

/**
 * Wash is holiday plus lead (activity_start–activity_end), or the week hits
 * tagged with that holiday. Calendar dates are always included. Ends on
 * holiday_end unless trailing weeks were still anomalous.
 */
export function hebcal_season_columns(
  windows: HolidayWindow[],
  holiday_key?: HolidayKey,
  weeks: WeekSeriesPoint[] = [],
  activity_seasons: ActivitySeason[] = []
): SeasonBand[] {
  const keys = paint_keys(windows, holiday_key);
  const bands: SeasonBand[] = [];
  for (const key of keys) {
    const marked = new Array(HEBREW_YEAR_WEEKS).fill(false);
    for (const window of windows) {
      if (window.holiday_key !== key) {
        continue;
      }
      mark_ymd_range(marked, window.holiday_start, window.holiday_end);
    }
    for (const season of activity_seasons) {
      if (season.holiday_key !== key) {
        continue;
      }
      mark_ymd_range(marked, season.activity_start, season.activity_end);
    }
    for (const week of weeks) {
      for (const hit of week.seasons || []) {
        if (hit.holiday_key !== key) {
          continue;
        }
        if (hit.window_start && hit.window_end) {
          mark_ymd_range(marked, hit.window_start, hit.window_end);
        }
      }
    }
    bands.push(...bands_from_marked(marked, key));
  }
  return bands;
}

/** Exact HebCal holiday dates only — the calendar core inside the activity wash. */
export function holiday_core_columns(
  windows: HolidayWindow[],
  holiday_key?: HolidayKey
): SeasonBand[] {
  const keys = paint_keys(windows, holiday_key);
  const bands: SeasonBand[] = [];
  for (const key of keys) {
    const marked = new Array(HEBREW_YEAR_WEEKS).fill(false);
    for (const window of windows) {
      if (window.holiday_key !== key) {
        continue;
      }
      mark_ymd_range(marked, window.holiday_start, window.holiday_end);
    }
    bands.push(...bands_from_marked(marked, key));
  }
  return bands;
}

export function season_legend_items(
  columns: SeasonBand[],
  activity_seasons?: ActivitySeason[],
  weeks?: WeekSeriesPoint[]
): Array<{ label: string; color: string }> {
  const seen = new Map<HolidayKey, string>();
  for (const band of columns) {
    for (const key of band.keys) {
      seen.set(key, HOLIDAY_LABELS[key] || band.label);
    }
  }
  if (seen.size === 0) {
    for (const season of activity_seasons || []) {
      seen.set(season.holiday_key, season.holiday_label || HOLIDAY_LABELS[season.holiday_key]);
    }
    for (const week of weeks || []) {
      for (const hit of week.seasons || []) {
        seen.set(hit.holiday_key, hit.holiday_label || HOLIDAY_LABELS[hit.holiday_key]);
      }
    }
  }
  return Array.from(seen.entries())
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([key, label]) => ({ label, color: HOLIDAY_COLORS[key] }));
}

export function add_six_days(ymd: string): string {
  return add_days_ymd(ymd, 6);
}

const ENGLISH_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function parse_ymd_parts(ymd: string): { y: number; mo: number; d: number } {
  return {
    y: parseInt(ymd.slice(0, 4), 10),
    mo: parseInt(ymd.slice(5, 7), 10),
    d: parseInt(ymd.slice(8, 10), 10),
  };
}

export function format_english_month_year(ymd: string): string {
  const { y, mo } = parse_ymd_parts(ymd);
  return `${ENGLISH_MONTHS[mo - 1]} ${y}`;
}

/** 15–21 Nisan 5784 style week label. */
export function format_hebrew_week_range(week_start: string): string {
  const start = gregorian_to_hebrew(week_start);
  const end = gregorian_to_hebrew(add_six_days(week_start));
  if (start.year === end.year && start.month_name === end.month_name) {
    return `${start.day}–${end.day} ${start.month_name} ${start.year}`;
  }
  if (start.year === end.year) {
    return `${start.day} ${start.month_name}–${end.day} ${end.month_name} ${start.year}`;
  }
  return `${format_hebrew_date(start)}–${format_hebrew_date(end)}`;
}

/** Mon 15–21 Apr 2024 style week label. */
export function format_english_week_range(week_start: string): string {
  const end = add_six_days(week_start);
  const a = parse_ymd_parts(week_start);
  const b = parse_ymd_parts(end);
  const start_month = ENGLISH_MONTHS[a.mo - 1];
  const end_month = ENGLISH_MONTHS[b.mo - 1];
  if (a.y === b.y && a.mo === b.mo) {
    return `${start_month} ${a.d}–${b.d}, ${a.y}`;
  }
  if (a.y === b.y) {
    return `${start_month} ${a.d}–${end_month} ${b.d}, ${a.y}`;
  }
  return `${start_month} ${a.d}, ${a.y}–${end_month} ${b.d}, ${b.y}`;
}

/** Indexes of local highs: one peak per run that is at least 40% of the line max. */
export function peak_indexes(values: number[]): number[] {
  const max = Math.max(0, ...values);
  if (max <= 0) {
    return [];
  }
  const floor = max * 0.4;
  const peaks: number[] = [];
  let i = 0;
  while (i < values.length) {
    if (values[i] < floor) {
      i += 1;
      continue;
    }
    let best = i;
    let j = i;
    while (j < values.length && values[j] >= floor) {
      if (values[j] > values[best]) {
        best = j;
      }
      j += 1;
    }
    peaks.push(best);
    i = j;
  }
  return peaks;
}

export interface WeekYearSold {
  year: string | number;
  sold: number;
}

export interface WeekYoyRow {
  year: number;
  sold: number;
  prior_year: number | null;
  prior_sold: number | null;
  change_pct: number | null;
  direction: AnomalyDirection | null;
}

export function format_signed_pct(change_pct: number): string {
  if (change_pct > 0) {
    return `+${change_pct}%`;
  }
  return `${change_pct}%`;
}

export function week_yoy_rows(years: WeekYearSold[]): WeekYoyRow[] {
  const sorted = [...years]
    .map((row) => ({ year: Number(row.year), sold: row.sold }))
    .sort((a, b) => a.year - b.year);
  return sorted.map((row, index) => {
    const prior = index > 0 ? sorted[index - 1] : null;
    if (!prior) {
      return {
        year: row.year,
        sold: row.sold,
        prior_year: null,
        prior_sold: null,
        change_pct: null,
        direction: null,
      };
    }
    const change_pct =
      prior.sold <= 0 ? (row.sold > 0 ? 100 : 0) : Math.round(((row.sold - prior.sold) / prior.sold) * 100);
    return {
      year: row.year,
      sold: row.sold,
      prior_year: prior.year,
      prior_sold: prior.sold,
      change_pct,
      direction: change_pct > 0 ? 'up' : change_pct < 0 ? 'down' : 'flat',
    };
  });
}

function format_week_cases(n: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n);
}

export function week_anomaly_lines(
  years: WeekYearSold[],
  scope: 'all' | 'product'
): string[] {
  const rows = week_yoy_rows(years);
  const lines = [scope === 'all' ? 'Whole week, all products:' : 'This product this week:'];
  for (const row of rows) {
    const sold = `${format_week_cases(row.sold)} sold`;
    if (row.prior_year == null || row.change_pct == null) {
      lines.push(`${row.year}: ${sold}`);
      continue;
    }
    lines.push(`${row.year}: ${sold} · ${format_signed_pct(row.change_pct)} vs ${row.prior_year}`);
  }
  return lines;
}

export interface HoverYearSlot {
  year: string | number;
  week_start: string | null;
  sold: number;
  produced: number;
}

/** Hover text: Hebrew + English dates and sold/produced for every year at this slot. */
export function hover_slot_lines(args: {
  years: HoverYearSlot[];
  season_names: string[];
  scope: 'all' | 'product';
}): string[] {
  const first_week = args.years.map((row) => row.week_start).find((week) => week) || null;
  const lines: string[] = [];
  if (first_week) {
    lines.push(`${format_english_week_range(first_week)} · ${format_hebrew_week_range(first_week)}`);
  }
  if (args.season_names.length > 0) {
    lines.push(args.season_names.join(', '));
  }
  lines.push(args.scope === 'all' ? 'Whole week, all products:' : 'This product this week:');
  for (const row of args.years) {
    const sold = `${format_week_cases(row.sold)} sold`;
    const produced = `${format_week_cases(row.produced)} produced`;
    if (row.week_start) {
      lines.push(
        `${row.year}: ${format_english_week_range(row.week_start)} · ${format_hebrew_week_range(row.week_start)} · ${sold} · ${produced}`
      );
    } else {
      lines.push(`${row.year}: ${sold} · ${produced}`);
    }
  }
  return lines;
}

export function year_change_words(row: SeasonAnomalyProduct | undefined): string {
  if (!row?.year_changes?.length) {
    return '';
  }
  return row.year_changes
    .map((change) => `${change.prior_year}→${change.year} ${format_signed_pct(change.change_pct)}`)
    .join(', ');
}

export function hebcal_labels_for_week(
  week_start: string | undefined,
  windows: HolidayWindow[] | undefined
): string[] {
  if (!week_start || !windows?.length) {
    return [];
  }
  const start = new Date(`${week_start}T12:00:00Z`);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const week_end = end.toISOString().slice(0, 10);
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const window of windows) {
    if (week_start <= window.holiday_end && window.holiday_start <= week_end) {
      if (!seen.has(window.holiday_label)) {
        seen.add(window.holiday_label);
        labels.push(window.holiday_label);
      }
    }
  }
  return labels;
}

export function season_year_lines(years: WeekYearSold[]): string[] {
  return week_yoy_rows(years).map((row) => {
    const sold = `${format_week_cases(row.sold)} sold`;
    if (row.prior_year == null || row.change_pct == null) {
      return `${row.year}: ${sold}`;
    }
    return `${row.year}: ${sold} · ${format_signed_pct(row.change_pct)} vs ${row.prior_year}`;
  });
}

export function season_sold_by_year(
  weeks: WeekSeriesPoint[],
  holiday_key: HolidayKey
): WeekYearSold[] {
  const by_year = new Map<number, number>();
  for (const week of weeks) {
    if (!(week.seasons || []).some((hit) => hit.holiday_key === holiday_key)) {
      continue;
    }
    const year = parseInt(week.week_start.slice(0, 4), 10);
    by_year.set(year, (by_year.get(year) || 0) + (week.sales_cases || 0));
  }
  return Array.from(by_year.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([year, sold]) => ({ year, sold }));
}

export function season_trend_words(
  row: SeasonAnomalyProduct | undefined,
  holiday_label: string
): string {
  const season = holiday_label || 'this season';
  if (!row) {
    return '';
  }
  if (row.trend === 'order_more') {
    return `${season} season: repeating rise — order more.`;
  }
  if (row.trend === 'order_less') {
    return `${season} season: repeating drop — order less.`;
  }
  if (row.trend === 'steady') {
    return `${season} season: no repeating year-to-year trend.`;
  }
  return `${season} season: one-year move only — ignored for ordering.`;
}

export const YEAR_LINE_COLORS = ['#1d4ed8', '#0f766e', '#7c3aed', '#b45309', '#be185d'];

export const DATA_START_DATE = '2023-01-01';

export function week_has_cases(week: WeekSeriesPoint): boolean {
  return week.sales_cases > 0 || week.production_cases > 0;
}

/** Drop weeks before 2023 and empty leading/trailing weeks so the x-axis is only real data. */
export function trim_weeks_to_data(weeks: WeekSeriesPoint[]): WeekSeriesPoint[] {
  const from_2023 = weeks.filter((week) => week.week_start >= DATA_START_DATE);
  let start = 0;
  while (start < from_2023.length && !week_has_cases(from_2023[start])) {
    start += 1;
  }
  let end = from_2023.length - 1;
  while (end >= start && !week_has_cases(from_2023[end])) {
    end -= 1;
  }
  if (start > end) {
    return [];
  }
  return from_2023.slice(start, end + 1);
}

/** First and last week indexes that have cases in any year. Empty range is start > end. */
export function trim_week_indexes(values_by_year: number[][]): { start: number; end: number } {
  const length = Math.max(0, ...values_by_year.map((row) => row.length));
  const has_data = (index: number) => values_by_year.some((row) => Number(row[index]) > 0);
  let start = 0;
  let end = length - 1;
  while (start <= end && !has_data(start)) {
    start += 1;
  }
  while (end >= start && !has_data(end)) {
    end -= 1;
  }
  return { start, end };
}

export function years_with_activity(weeks: WeekSeriesPoint[]): string[] {
  const years = new Set<string>();
  for (const week of weeks) {
    if (week.week_start >= DATA_START_DATE && week_has_cases(week)) {
      years.add(week.week_start.slice(0, 4));
    }
  }
  return Array.from(years).sort();
}
